import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, fetchMe } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import {
  LIVE_FLUSH_INTERVAL_MS, batchTouches, createLiveBatcher, keyIsLiveFor, type LiveBatch, type LiveEvent, type LiveKind,
} from '@/lib/liveUpdates';
import { startLiveStream } from '@/lib/liveStream';

/**
 * Live cards (Warren, 28 Oct 2026): one EventSource on /admin/live for the
 * whole app, mounted inside QueryClientProvider (main.tsx).
 *
 * Each event the Api forwards (already scoped to this viewer) is batched,
 * at most one round per tab every 5 s, and the react-query queries whose keys
 * LIVE_QUERY_KEYS (lib/liveUpdates.ts) assigns to the kinds that arrived
 * are invalidated: the ones on screen refetch, the rest refetch when next
 * shown. Pages that load into local state instead of react-query subscribe
 * with useLiveReload below.
 *
 * Connection: opened with the access token the auth store holds, reopened
 * when that token changes (refresh, sign-in) and closed on sign-out. A
 * stream the browser gave up on (a 401, the Api's planned close with an
 * expired token) is reopened with backoff, 1 s to 30 s; when the stored
 * token has expired too, an authenticated read runs the refresh hook first
 * and the new token reconnects through the store.
 *
 * Gaps: on every reconnect everything live is refetched once, because
 * events during the gap are gone. While the stream is down, or the Api
 * reports its live bus is not live (in the ack, or later as `degraded`),
 * the same catch-up runs once a minute in a visible tab: the safety net
 * that keeps a page from going stale without a stream. When the Api says
 * `recovered`, polling stops and everything live is refetched once. With a
 * healthy stream nothing polls. The connection itself is lib/liveStream.ts.
 */

type Listener = (batch: LiveBatch) => void;

interface LiveHub {
  subscribe: (listener: Listener) => () => void;
}

const LiveContext = createContext<LiveHub | null>(null);

export function LiveUpdatesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const listeners = useMemo(() => new Set<Listener>(), []);
  const hub = useMemo<LiveHub>(() => ({
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  }), [listeners]);
  // A building staff or supplier login is refused the stream by the Api (its
  // boundaries, 28 Oct 2026), so it is never opened for one: no refused
  // reconnect every 30 s. Their pages poll.
  const noStream = useAuthStore((s) => s.user?.facilities_only === true || s.user?.template === 'supplier_account'
    || s.user?.supplier_only === true);

  useEffect(() => {
    if (noStream) return;
    const batcher = createLiveBatcher({
      intervalMs: LIVE_FLUSH_INTERVAL_MS,
      onFlush: (batch) => {
        // Every matching query goes stale; react-query refetches the active
        // ones now (refetchType defaults to 'active').
        void queryClient.invalidateQueries({ predicate: (q) => keyIsLiveFor(q.queryKey, batch.kinds) });
        for (const listener of listeners) listener(batch);
      },
    });
    const stream = startLiveStream({ onEvent: batcher.add, onCatchUp: batcher.catchUp }, {
      url: (token) => `${API_BASE_URL}/admin/live?token=${encodeURIComponent(token)}`,
      openSource: (url) => new EventSource(url),
      getToken: () => useAuthStore.getState().accessToken,
      onTokenChange: (listener) => useAuthStore.subscribe((state, prev) => listener(state.accessToken, prev.accessToken)),
      refreshToken: () => { void fetchMe().catch(() => null); },
      isVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
    });
    return () => {
      stream.stop();
      batcher.dispose();
    };
  }, [queryClient, listeners, noStream]);

  return <LiveContext.Provider value={hub}>{children}</LiveContext.Provider>;
}

/**
 * For pages that load into local state: call `reload` when a batch touches
 * these kinds (and `match`, when given). While `enabled` is false (a form
 * is open, say) a touching batch is remembered and the reload runs once
 * when it turns true again, so editing never gets clobbered and nothing is
 * missed.
 */
export function useLiveReload(opts: {
  kinds: readonly LiveKind[];
  match?: (e: LiveEvent) => boolean;
  enabled?: boolean;
  reload: () => void;
}): void {
  const hub = useContext(LiveContext);
  const latest = useRef(opts);
  const missed = useRef(false);
  useEffect(() => { latest.current = opts; });

  useEffect(() => {
    if (!hub) return undefined;
    return hub.subscribe((batch) => {
      const o = latest.current;
      if (!batchTouches(batch, o.kinds, o.match)) return;
      if (o.enabled === false) {
        missed.current = true;
        return;
      }
      o.reload();
    });
  }, [hub]);

  const enabled = opts.enabled !== false;
  useEffect(() => {
    if (enabled && missed.current) {
      missed.current = false;
      latest.current.reload();
    }
  }, [enabled]);
}
